import {
  Box,
  chakra,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate, useParams } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  Ellipsis,
  EyeIcon,
  GripVerticalIcon,
  MessagesSquareIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import { PublishedStatusBadge } from '../../navbar/PublishedStatusBadge';
import {
  TranslationCoverageBadge,
  TranslationCoverageBadgeSkeleton,
  type ResourceCoverageLike,
} from '../../components/translations/TranslationCoverageBadge';
import { useCallback, useMemo, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

// Type for learning resources returned from the backend
type LearningResourcesWithRelations = NonNullable<
  RouterOutputs['unitsOfCompetency']['resourcesByIdAndKind']
>['learningResources'][number];

const List = chakra(Reorder.Group);
const ListItem = chakra(Reorder.Item);

export function ConversationalGuideSection({
  data,
  onPreview,
  coverageByResourceId,
  coverageLoading = false,
}: {
  data: LearningResourcesWithRelations[];
  onPreview: (resourceId: string) => void;
  coverageByResourceId?: Map<string, ResourceCoverageLike>;
  // Rows show a placeholder while loading, rather than flashing "Not translated".
  coverageLoading?: boolean;
}) {
  const { unitId } = useParams();
  const toast = useToast();
  const [isDeleteConfirmationAlertOpen, setIsDeleteConfirmationAlertOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);

  if (!unitId) {
    throw new Error('unitId is required');
  }
  const utils = trpcAdmin.useUtils();

  // resources is a subsection of data, that can be reordered at runtime
  const [resources, setResources] = useState<any[]>([]);

  // if data changes, we may need to update the resources
  // this happens when the data is fetched for the first time
  // and after the data is refetched after reordering
  useMemo(() => {
    if (data) {
      setResources(data);
    }
  }, [data]);

  const archiveResource = trpcAdmin.resources.archive.useMutation();

  const reorderResources = trpcAdmin.unitsOfCompetency.reorderResources.useMutation({
    async onMutate(newOrder) {
      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await utils.unitsOfCompetency.byId.cancel();

      // Get the data from the queryCache
      const prevData = utils.unitsOfCompetency.byId.getData();

      // Optimistically update the data with our new order
      utils.unitsOfCompetency.byId.setData(unitId, (prevData) => {
        if (!prevData) {
          return;
        }

        // Create a new array with the same elements as the previous learningResources
        const updatedLearningResources = [...prevData.learningResources];

        // Reorder the resources included in the newOrder.resourceIds array
        newOrder.resourceIds.forEach((resourceId, newIndex) => {
          const oldIndex = updatedLearningResources.findIndex(
            (resource) => resource.resource_id === resourceId,
          );
          if (oldIndex !== -1 && oldIndex !== newIndex) {
            const [removed] = updatedLearningResources.splice(oldIndex, 1);
            if (removed) {
              updatedLearningResources.splice(newIndex, 0, removed);
            }
          }
        });

        // Combine the reordered resources with the non-reordered resources
        const nonReorderedResources = prevData.learningResources.filter(
          (resource) => !newOrder.resourceIds.includes(resource.resource_id),
        );
        const combinedLearningResources = [...updatedLearningResources, ...nonReorderedResources];

        return {
          ...prevData,
          learningResources: combinedLearningResources,
        };
      });

      // Return the previous data so we can revert if something goes wrong
      return { prevData };
    },
    onError(_err, _newOrder, ctx) {
      // If the mutation fails, use the context-value from onMutate
      if (ctx) {
        utils.unitsOfCompetency.byId.setData(unitId, ctx.prevData);
      }
    },
    onSettled() {
      utils.unitsOfCompetency.byId.invalidate();
    },
  });

  const saveReorder = useCallback(() => {
    reorderResources.mutate({
      unitId: unitId,
      resourceIds: resources.map((resource) => resource.resource_id),
    });
  }, [reorderResources, unitId, resources]);

  const onClickDeleteResource = useCallback(
    (resourceId: string) => {
      if (!data) {
        return;
      }
      archiveResource.mutate(resourceId, {
        onSuccess: () => {
          // remove the resource from the resources list
          const updatedResources = resources.filter(
            (resource) => resource.resource_id !== resourceId,
          );

          // update the order of the resources
          reorderResources.mutate({
            unitId: unitId,
            resourceIds: updatedResources.map((resource) => resource.resource_id),
          });

          toast({
            title: t`Conversational flow deleted successfully.`,
            status: 'success',
            isClosable: true,
          });
        },
      });
    },
    [archiveResource, data, reorderResources, resources, toast, unitId],
  );

  return (
    <>
      <Stack spacing="4" w="full" bg="bg.surface">
        {resources ? (
          <List values={resources} onReorder={setResources} listStyleType="none">
            <Stack spacing="4" width="full">
              {resources.map((item: LearningResourcesWithRelations) => (
                <Item
                  key={item.resource_id}
                  item={item}
                  saveReorder={saveReorder}
                  setSelectedResourceId={setSelectedResourceId}
                  setIsDeleteConfirmationAlertOpen={setIsDeleteConfirmationAlertOpen}
                  onPreview={onPreview}
                  coverageLoading={coverageLoading}
                  // Undefined map = feature off = no badge. Otherwise the resource's
                  // coverage, or empty when the batch query hasn't filled it yet.
                  coverage={
                    coverageByResourceId === undefined
                      ? undefined
                      : (coverageByResourceId.get(item.resource_id) ?? {
                          currentPublishedVersionId: null,
                          rows: [],
                        })
                  }
                />
              ))}
            </Stack>
          </List>
        ) : null}
      </Stack>

      <ConfirmationDialog
        isOpen={isDeleteConfirmationAlertOpen}
        onClose={() => setIsDeleteConfirmationAlertOpen(false)}
        title={<Trans>Delete Conversational Flow</Trans>}
        body={
          <Box>
            <Trans>
              You are about to delete this conversational flow. Are you sure you want to continue?
            </Trans>
            {selectedResourceId ? (
              <Box
                mt={4}
                p={4}
                borderColor="danger.subtle"
                bg="bg.danger-subtle"
                borderRadius="8px"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {
                    resources.find((unit) => unit.resource_id === selectedResourceId)?.resource
                      .title
                  }
                </Text>
              </Box>
            ) : null}
          </Box>
        }
        onConfirm={() => {
          if (selectedResourceId) {
            onClickDeleteResource(selectedResourceId);
          }
          setIsDeleteConfirmationAlertOpen(false);
        }}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </>
  );
}

function Item({
  item,
  saveReorder,
  setSelectedResourceId,
  setIsDeleteConfirmationAlertOpen,
  onPreview,
  coverage,
  coverageLoading,
}: {
  item: LearningResourcesWithRelations;
  saveReorder: () => void;
  setSelectedResourceId: (_resourceId: string) => void;
  setIsDeleteConfirmationAlertOpen: (_isOpen: boolean) => void;
  onPreview: (resourceId: string) => void;
  // Per-resource translation coverage for this row's badge; undefined when the
  // translations feature is off.
  coverage?: ResourceCoverageLike;
  // True while coverage is loading: show a placeholder instead of the badge.
  coverageLoading?: boolean;
}) {
  const dragControls = useDragControls();

  const navigate = useNavigate();

  const editResource = useCallback(() => {
    if (item.resource_id !== null) {
      navigate(`/resources/${item.resource_id}`);
    }
  }, [item, navigate]);

  if (!item.resource) {
    return null;
  }

  return (
    <ListItem
      key={item.resource_id}
      value={item}
      position="relative"
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={saveReorder}
    >
      <HStack gap="4px" align="center">
        <HStack
          p="8px"
          borderWidth="1px"
          borderColor="border.emphasis"
          borderRadius="8px"
          bg="bg.canvas"
          gap="16px"
          w="full"
          justify="start"
        >
          <HStack
            gap="16px"
            align="center"
            w="full"
            flex="1"
            onClick={editResource}
            cursor="pointer"
          >
            <Icon as={MessagesSquareIcon} color="fg.emphasis" boxSize="32px" strokeWidth="1px" />
            <Text fontSize="lg" lineHeight="7" fontWeight="medium" color="fg.default">
              {item.resource.title}
            </Text>
          </HStack>
          <HStack gap="8px">
            <PublishedStatusBadge isPublished={item.resource.last_published_version_id != null} />
            {coverageLoading ? (
              <TranslationCoverageBadgeSkeleton />
            ) : coverage ? (
              <TranslationCoverageBadge coverage={coverage} />
            ) : null}
            <HStack gap={0}>
              <Tooltip label={t`Preview`} closeOnClick>
                <IconButton
                  aria-label={t`Preview`}
                  size="sm"
                  variant="ghost"
                  icon={<EyeIcon width="20px" height="20px" />}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    onPreview(item.resource_id);
                  }}
                />
              </Tooltip>
              <Menu gutter={12}>
                <MenuButton
                  as={IconButton}
                  aria-label={t`More options`}
                  size="sm"
                  variant="ghost"
                  icon={<Ellipsis width="20px" height="20px" />}
                  onClick={() => setSelectedResourceId(item.resource_id)}
                />
                <MenuList fontSize="md">
                  <MenuItem icon={<PencilIcon size="16px" />} onClick={editResource}>
                    {t`Edit`}
                  </MenuItem>
                  <MenuItem
                    icon={<Trash2Icon size="16px" />}
                    onClick={() => setIsDeleteConfirmationAlertOpen(true)}
                  >
                    {t`Delete`}
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </HStack>
        </HStack>
        <IconButton
          _hover={{ color: 'brand.600' }}
          icon={<Icon as={GripVerticalIcon} />}
          cursor="grab"
          size="sm"
          variant="tertiary"
          aria-label={t`Reorder learning material`}
          onPointerDown={(e) => dragControls.start(e)}
        />
      </HStack>
    </ListItem>
  );
}
